import React, { useState, useEffect } from 'react';
import { RefreshCw, Wifi, WifiOff, AlertCircle } from 'lucide-react';
import { syncManager } from '../services/syncManager';
import { sqliteSyncManager } from '../services/sqliteSyncManager';
import { Capacitor } from '@capacitor/core';
import { isFeatureEnabled } from '../config/appConfig';

interface SyncStatus {
  isSyncing: boolean;
  lastSync: number | null;
  pendingItems: number;
  error: string | null;
}

const SyncButton: React.FC = () => {
  const [status, setStatus] = useState<SyncStatus>(syncManager.getStatus());
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [useSQLite, setUseSQLite] = useState(false);
  const syncEnabled = isFeatureEnabled('enableSync');

  useEffect(() => {
    // Hide sync button if sync feature is disabled
    if (!syncEnabled) return;

    // Determine which sync manager to use
    const platform = Capacitor.getPlatform();
    const isNative = platform === 'android' || platform === 'ios';
    setUseSQLite(isNative);

    const manager = isNative ? sqliteSyncManager : syncManager;
    setStatus(manager.getStatus());

    const unsubscribe = manager.subscribe((newStatus) => {
      setStatus(newStatus);
    });

    const handleOnline = () => {
      setIsOnline(true);
      // Auto-sync when coming back online
      manager.sync();
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncEnabled]);

  // Hide sync button if sync feature is disabled
  if (!syncEnabled) {
    return null;
  }

  const handleSync = () => {
    const manager = useSQLite ? sqliteSyncManager : syncManager;
    manager.sync();
  };

  const formatLastSync = (timestamp: number | null) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="flex items-center gap-2">
      {/* Connection Status */}
      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
        isOnline 
          ? 'bg-green-50 text-green-700 border border-green-200' 
          : 'bg-red-50 text-red-700 border border-red-200'
      }`}>
        {isOnline ? (
          <>
            <Wifi className="w-3.5 h-3.5" />
            Online
          </>
        ) : (
          <>
            <WifiOff className="w-3.5 h-3.5" />
            Offline
          </>
        )}
      </div>

      {/* Sync Button */}
      <button
        onClick={handleSync}
        disabled={status.isSyncing || !isOnline}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
          status.isSyncing
            ? 'bg-blue-50 text-blue-700 border border-blue-200'
            : status.error
            ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
            : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
        } ${!isOnline ? 'opacity-50 cursor-not-allowed' : ''}`}
        title={status.error || 'Sync data'}
      >
        <RefreshCw className={`w-3.5 h-3.5 ${status.isSyncing ? 'animate-spin' : ''}`} />
        {status.isSyncing ? 'Syncing...' : 'Sync'}
      </button>

      {/* Pending Items Indicator */}
      {status.pendingItems > 0 && (
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
          <AlertCircle className="w-3.5 h-3.5" />
          {status.pendingItems} pending
        </div>
      )}

      {/* Last Sync Time */}
      <div className="text-xs text-gray-500">
        Last sync: {formatLastSync(status.lastSync)}
      </div>
    </div>
  );
};

export default SyncButton;
